import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Paper,
  Alert,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import {
  OrganizationMember,
  OrganizationRole,
  getRoleLabel,
  getRoleColor,
  isRoleEqual,
} from '../../services/organizationApi';

interface MemberManagementProps {
  members: OrganizationMember[];
  onRemoveMember: (memberId: string) => void;
  onUpdateRole: (member: OrganizationMember) => void;
  canManage: boolean;
  canView: boolean;
}

const MemberManagement: React.FC<MemberManagementProps> = ({
  members,
  onRemoveMember,
  onUpdateRole,
  canManage,
  canView,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<OrganizationMember | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, member: OrganizationMember) => {
    setAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMember(null);
  };

  const handleRemoveMember = () => {
    if (selectedMember) {
      onRemoveMember(selectedMember.id);
      handleMenuClose();
    }
  };

  const handleUpdateRole = () => {
    if (selectedMember) {
      onUpdateRole(selectedMember);
      handleMenuClose();
    }
  };

  const getCurrentUserId = (): string => {
    return localStorage.getItem('gamora_user_id') || '';
  };

  const isCurrentUser = (member: OrganizationMember): boolean => {
    return member.user_id === getCurrentUserId();
  };

  const canModifyMember = (member: OrganizationMember): boolean => {
    if (!canManage) return false;
    if (isCurrentUser(member)) return false; // Can't modify self
    if (isRoleEqual(member.role, OrganizationRole.OWNER)) return false; // Can't modify owner
    return true;
  };

  const getRoleIcon = (role: OrganizationRole | string | number) => {
    if (isRoleEqual(role, OrganizationRole.OWNER)) {
      return <SecurityIcon />;
    } else if (isRoleEqual(role, OrganizationRole.ADMIN)) {
      return <AdminIcon />;
    } else {
      return <PersonIcon />;
    }
  };

  const sortedMembers = [...members].sort((a, b) => {
    // Sort by role (owner first, then admin, then others)
    if (!isRoleEqual(a.role, b.role)) {
      const roleA = typeof a.role === 'string' ? parseInt(a.role) : a.role;
      const roleB = typeof b.role === 'string' ? parseInt(b.role) : b.role;
      return roleA - roleB;
    }
    // Then sort by username
    return a.user.username.localeCompare(b.user.username);
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Organization Members ({members.length})
        </Typography>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<PersonIcon />}
            disabled={!canManage}
          >
            Invite Member
          </Button>
        )}
      </Box>

      {/* Permission Notice */}
      {!canView && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>Access Denied:</strong> You don't have permission to view organization members.
        </Alert>
      )}
      
      {canView && !canManage && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>View Only:</strong> You can view members but only organization owners can manage them, 
          update roles, or remove members from the organization.
        </Alert>
      )}

      {/* Members List */}
      {canView && (
        <Paper>
          <List>
            {sortedMembers.map((member, index) => (
            <React.Fragment key={member.id}>
              <ListItem>
                <ListItemIcon>
                  {getRoleIcon(member.role)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">
                        {member.user.username}
                        {isCurrentUser(member) && (
                          <Chip
                            label="You"
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {member.user.email}
                        {member.user.company && ` • ${member.user.company}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Joined {new Date(member.joined_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={getRoleLabel(member.role)}
                    sx={{
                      backgroundColor: getRoleColor(member.role),
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                    size="small"
                  />
                  {canModifyMember(member) && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, member)}
                      disabled={!canManage}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </Box>
              </ListItem>
              {index < sortedMembers.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
        </Paper>
      )}

      {/* Member Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleUpdateRole}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Update Role
        </MenuItem>
        <MenuItem onClick={handleRemoveMember} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          Remove Member
        </MenuItem>
      </Menu>

      {/* Empty State */}
      {members.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Members Yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start by inviting members to your organization.
          </Typography>
        </Paper>
      )}

      {/* Role Legend */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Role Permissions
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label="Owner"
              sx={{
                backgroundColor: getRoleColor(OrganizationRole.OWNER),
                color: 'white',
                fontWeight: 'bold',
              }}
              size="small"
            />
            <Typography variant="body2">
              Full control - can manage organization, members, and delete organization
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label="Admin"
              sx={{
                backgroundColor: getRoleColor(OrganizationRole.ADMIN),
                color: 'white',
                fontWeight: 'bold',
              }}
              size="small"
            />
            <Typography variant="body2">
              Administrative access - can manage content and basic operations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label="Member"
              sx={{
                backgroundColor: getRoleColor(OrganizationRole.MEMBER),
                color: 'white',
                fontWeight: 'bold',
              }}
              size="small"
            />
            <Typography variant="body2">
              Standard access - can view and contribute to organization content
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label="Viewer"
              sx={{
                backgroundColor: getRoleColor(OrganizationRole.VIEWER),
                color: 'white',
                fontWeight: 'bold',
              }}
              size="small"
            />
            <Typography variant="body2">
              Read-only access - can view organization content
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MemberManagement;
